/**
 * Fund status — the labels, the header-badge palette and which actions each
 * status offers. One place for the UI; `save_fund` (20261006210000) enforces
 * the actual transitions, so a button shown here can still be refused there.
 *
 * - `pipeline ↔ not_awarded` and `awarded ↔ closed` are plain status writes.
 * - `pipeline → awarded` is the award (an award date + installments → one AR
 *   invoice per installment), or a plain status write for a fund with no
 *   installments.
 * - `cancelled` has no way out in P1.
 */
import type { FundStatus } from '@releve/shared';

import type { TranslateFn } from './fundGridCells';

/** The statuses a fund can move to by a plain status write (no award). */
export const FUND_STATUS_TRANSITIONS: Record<FundStatus, readonly FundStatus[]> = {
  pipeline: ['not_awarded'],
  not_awarded: ['pipeline'],
  awarded: ['closed'],
  closed: ['awarded'],
  cancelled: [],
};

interface FundStatusActions {
  /** The header's "Mark as awarded" (opens the fund form in Awarded mode). */
  markAwarded: boolean;
  /**
   * The grid toggle: the status it writes, or null when the status has no
   * toggle (awarded → closed = "Close", closed → awarded = "Reopen").
   */
  toggleTo: FundStatus | null;
}

/** Which actions the fund header and the grid's expanded row show, per status. */
export const FUND_STATUS_ACTIONS: Record<FundStatus, FundStatusActions> = {
  pipeline: { markAwarded: true, toggleTo: null },
  not_awarded: { markAwarded: false, toggleTo: null },
  awarded: { markAwarded: false, toggleTo: 'closed' },
  closed: { markAwarded: false, toggleTo: 'awarded' },
  cancelled: { markAwarded: false, toggleTo: null },
};

const FUND_STATUSES: readonly FundStatus[] = [
  'pipeline',
  'awarded',
  'not_awarded',
  'cancelled',
  'closed',
];

/** Translated status label; an unknown value falls back to itself. */
export function fundStatusLabel(status: unknown, t: TranslateFn): string {
  const key = String(status ?? '');
  if (!key) return '';
  return (FUND_STATUSES as readonly string[]).includes(key) ? t(`funds.status.${key}`) : key;
}

/** Header badge (docs/STYLES.md "General Status") — never used in grid cells. */
export const FUND_STATUS_BADGE: Record<
  FundStatus,
  { variant: 'outline' | 'secondary'; className?: string }
> = {
  pipeline: { variant: 'outline', className: 'bg-info/10 text-info border-info/30' },
  awarded: { variant: 'outline', className: 'bg-success/10 text-success border-success/30' },
  not_awarded: { variant: 'secondary' },
  cancelled: { variant: 'outline', className: 'bg-destructive/10 text-destructive border-destructive/30' },
  closed: { variant: 'secondary' },
};

/** The fund detail route, relative to the org base path. */
export const FUNDS_LIST_PATH = '/accounting/funds';
export const fundDetailPath = (code: string) => `${FUNDS_LIST_PATH}/${encodeURIComponent(code)}`;
